import React, { useState, useEffect, useCallback, useRef, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useParams, useSearchParams } from 'react-router-dom';
import './App.css';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { Check, UserCircle, MapPin, Calendar, Upload, PenTool, UserPlus, ArrowRight, Camera, Loader2 } from 'lucide-react';
import countries from 'i18n-iso-countries';
import countriesEn from 'i18n-iso-countries/langs/en.json';
countries.registerLocale(countriesEn);

// Lazy-loaded routes (code splitting)
const AuthLogin = lazy(() => import('./components/AuthLogin'));
const AdminDashboard = lazy(() => import('./components/AdminDashboard'));
const FormDetails = lazy(() => import('./components/FormDetails'));
const LandingPage = lazy(() => import('./components/LandingPage'));

// 1. Global styles
const globalStyles = `
  *,
  *::before,
  *::after {
    box-sizing: border-box;
  }

  html, body {
    margin: 0;
    padding: 0;
    width: 100%;
    overflow-x: hidden;
    font-family: sans-serif;
  }

  img, video {
    max-width: 100%;
    height: auto;
  }

  /* Improve date input styling on mobile */
  input[type="date"] {
    appearance: none;
    -webkit-appearance: none;
    position: relative;
  }

  /* Hide the calendar icon on date inputs */
  input[type="date"]::-webkit-calendar-picker-indicator {
    background: transparent;
    width: 100%;
    height: 100%;
    position: absolute;
    top: 0;
    left: 0;
    color: transparent;
    cursor: pointer;
  }

  @media (max-width: 640px) {
    .mobile-block {
      display: block !important;
      width: 100% !important;
    }

    .mobile-full {
      width: 100% !important;
      max-width: 100% !important;
      padding-left: 0 !important;
      padding-right: 0 !important;
    }

    .container {
      width: 100% !important;
      padding-left: 8px !important;
      padding-right: 8px !important;
    }
  }
`;

// ------------------------------------------------------
// File Upload Component
// ------------------------------------------------------
const FileUpload = ({ onFileUpload, currentFile }) => {
  const fileInputRef = useRef(null);

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      const reader = new FileReader();

      reader.onloadend = () => {
        onFileUpload({
          file: file,
          name: file.name,
          size: file.size,
          mimeType: file.type,
          base64: reader.result
        });
      };

      reader.readAsDataURL(file);
    }
  };

  return (
    <div style={{ width: '100%' }}>
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileChange}
        accept="image/*"
        style={{ display: 'none' }}
        capture="environment"
      />

      {currentFile ? (
        <div style={{
          border: '1px solid #E5E7EB',
          borderRadius: '8px',
          padding: '16px',
          marginBottom: '8px',
          backgroundColor: '#F9FAFB'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            marginBottom: '12px',
            flexDirection: window.innerWidth < 640 ? 'column' : 'row'
          }}>
            {currentFile.base64 && (
              <div style={{
                marginRight: window.innerWidth < 640 ? '0' : '16px',
                marginBottom: window.innerWidth < 640 ? '12px' : '0',
                width: window.innerWidth < 640 ? '100%' : '120px',
                height: window.innerWidth < 640 ? 'auto' : '80px',
                borderRadius: '8px',
                overflow: 'hidden',
                border: '1px solid #E5E7EB'
              }}>
                <img
                  src={currentFile.base64}
                  alt="ID Document"
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover'
                  }}
                />
              </div>
            )}
            <div style={{ flex: '1' }}>
              <p style={{ margin: '0 0 4px 0', fontSize: '14px', fontWeight: '500' }}>
                {currentFile.name || 'Document uploaded'}
              </p>
              <p style={{ margin: '0', fontSize: '12px', color: '#6B7280' }}>
                {currentFile.size ? `${Math.round(currentFile.size / 1024)} KB` : ''}
              </p>
            </div>
          </div>

          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              type="button"
              onClick={() => fileInputRef.current.click()}
              style={{
                padding: '8px 12px',
                backgroundColor: '#F3F4F6',
                color: '#1F2937',
                border: 'none',
                borderRadius: '6px',
                fontSize: '14px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <Camera style={{ width: '16px', height: '16px', marginRight: '4px' }} />
              Replace
            </button>
            <button
              type="button"
              onClick={() => onFileUpload(null)}
              style={{
                padding: '8px 12px',
                backgroundColor: '#FEE2E2',
                color: '#B91C1C',
                border: 'none',
                borderRadius: '6px',
                fontSize: '14px',
                cursor: 'pointer'
              }}
            >
              Remove
            </button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => fileInputRef.current.click()}
          style={{
            width: '100%',
            padding: '24px 16px',
            backgroundColor: '#F9FAFB',
            border: '2px dashed #E5E7EB',
            borderRadius: '8px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            cursor: 'pointer'
          }}
        >
          <div style={{
            backgroundColor: '#EBF5FF',
            borderRadius: '50%',
            width: '64px',
            height: '64px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: '16px'
          }}>
            <Camera size={32} color="#3B82F6" />
          </div>
          <p style={{ margin: '0 0 4px 0', fontWeight: '500', fontSize: '16px' }}>Take photo of ID document</p>
          <p style={{ margin: '0', fontSize: '14px', color: '#6B7280', textAlign: 'center' }}>
            Passport, ID card or driver's license
          </p>
        </button>
      )}
    </div>
  );
};

// ------------------------------------------------------
// Signature Pad Component
// ------------------------------------------------------
const SignaturePad = ({ onSignature, currentSignature }) => {
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [context, setContext] = useState(null);

  // Initialise canvas and handle resize
  useEffect(() => {
    const canvas = canvasRef.current;
    const container = containerRef.current;

    const setCanvasSize = () => {
      if (canvas && container) {
        const containerWidth = container.clientWidth - 2; // Account for border
        canvas.width = containerWidth;
        canvas.height = 200;

        const ctx = canvas.getContext('2d');
        ctx.fillStyle = '#fff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.strokeStyle = '#000000';
        ctx.lineWidth = 2;
        ctx.lineCap = 'round';

        if (currentSignature) {
          const img = new Image();
          img.onload = () => {
            ctx.drawImage(img, 0, 0);
          };
          img.src = currentSignature;
        }
        setContext(ctx);
      }
    };

    setCanvasSize();
    window.addEventListener('resize', setCanvasSize);
    return () => window.removeEventListener('resize', setCanvasSize);
  }, [currentSignature]);

  useEffect(() => {
    const handleTouchMove = (e) => {
      if (isDrawing) {
        e.preventDefault();
        e.stopPropagation();
      }
    };
    document.addEventListener('touchmove', handleTouchMove, { passive: false });
    return () => document.removeEventListener('touchmove', handleTouchMove);
  }, [isDrawing]);

  const getCoordinates = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    if (e.touches && e.touches[0]) {
      return {
        offsetX: e.touches[0].clientX - rect.left,
        offsetY: e.touches[0].clientY - rect.top
      };
    }
    return {
      offsetX: e.clientX - rect.left,
      offsetY: e.clientY - rect.top
    };
  };

  const startDrawing = (e) => {
    e.preventDefault();
    const { offsetX, offsetY } = getCoordinates(e);
    if (context) {
      context.beginPath();
      context.moveTo(offsetX, offsetY);
      setIsDrawing(true);
    }
  };

  const draw = (e) => {
    e.preventDefault();
    if (!isDrawing || !context) return;
    const { offsetX, offsetY } = getCoordinates(e);
    context.lineTo(offsetX, offsetY);
    context.stroke();
  };

  const stopDrawing = () => {
    if (isDrawing && context) {
      context.closePath();
      setIsDrawing(false);
      const signatureData = canvasRef.current.toDataURL();
      onSignature(signatureData);
    }
  };

  const clearSignature = () => {
    if (context && canvasRef.current) {
      context.fillStyle = '#fff';
      context.fillRect(0, 0, canvasRef.current.width, canvasRef.current.height);
      context.strokeStyle = '#000000';
      context.lineWidth = 2;
      onSignature(null);
    }
  };

  return (
    <div ref={containerRef} style={{ width: '100%', touchAction: 'none' }}>
      <div
        style={{
          border: '1px solid #E5E7EB',
          borderRadius: '8px',
          overflow: 'hidden',
          touchAction: 'none',
          position: 'relative',
          backgroundColor: '#fff',
          width: '100%'
        }}
      >
        <canvas
          ref={canvasRef}
          width="100%"
          height="200"
          style={{
            width: '100%',
            height: '200px',
            touchAction: 'none',
            display: 'block',
            cursor: 'crosshair'
          }}
          onMouseDown={startDrawing}
          onMouseMove={draw}
          onMouseUp={stopDrawing}
          onMouseLeave={stopDrawing}
          onTouchStart={startDrawing}
          onTouchMove={draw}
          onTouchEnd={stopDrawing}
        />
        <button
          type="button"
          onClick={clearSignature}
          style={{
            position: 'absolute',
            top: '8px',
            right: '8px',
            backgroundColor: 'rgba(0, 0, 0, 0.1)',
            border: 'none',
            borderRadius: '4px',
            padding: '4px 8px',
            fontSize: '12px',
            cursor: 'pointer'
          }}
        >
          Clear
        </button>
      </div>
      <p style={{ fontSize: '12px', color: '#6B7280', marginTop: '8px', textAlign: 'center' }}>
        {!currentSignature ? 'Draw your signature above' : 'Signature captured'}
      </p>
    </div>
  );
};

// ------------------------------------------------------
// Remaining helper components and MultiPageGuestForm ...
// ------------------------------------------------------
// (The rest of the code remains unchanged and is included below for completeness)

// Helper InputField component
const InputField = ({
  label,
  name,
  type,
  value,
  onChange,
  required = false,
  disabled = false,
  min = '',
  readOnly = false,
  placeholder = '',
  error,
  showErrors = false
}) => {
  const isValid = value && !error && required;
  return (
    <div style={{ width: '100%', maxWidth: '100%', marginBottom: '16px' }}>
      <label
        style={{
          display: 'block',
          fontSize: '14px',
          fontWeight: '500',
          color: showErrors && error ? '#B91C1C' : '#374151',
          marginBottom: '4px'
        }}
      >
        {label} {required && <span style={{ color: '#EF4444' }}>*</span>}
      </label>
      {type === 'date' ? (
        <div style={{ position: 'relative', width: '100%' }}>
          <input
            type={type}
            name={name}
            value={value}
            onChange={onChange}
            style={{
              width: '100%',
              padding: '10px',
              paddingRight: isValid ? '32px' : '10px',
              border: `1px solid ${showErrors && error ? '#B91C1C' : isValid ? '#10B981' : '#E5E7EB'}`,
              borderRadius: '8px',
              backgroundColor: disabled ? '#F3F4F6' : 'white',
              color: value ? '#000' : '#9CA3AF'
            }}
            required={required}
            disabled={disabled}
            min={min}
            readOnly={readOnly}
            placeholder={placeholder || 'Select date'}
            onFocus={(e) => e.target.showPicker && e.target.showPicker()}
          />
          {!value && (
            <div
              style={{
                position: 'absolute',
                right: '10px',
                top: '50%',
                transform: 'translateY(-50%)',
                pointerEvents: 'none'
              }}
            >
              <Calendar size={16} color="#9CA3AF" />
            </div>
          )}
          {isValid && (
            <div
              style={{
                position: 'absolute',
                right: '10px',
                top: '50%',
                transform: 'translateY(-50%)',
                pointerEvents: 'none'
              }}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#10B981"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                <polyline points="22 4 12 14.01 9 11.01" />
              </svg>
            </div>
          )}
        </div>
      ) : (
        <div style={{ position: 'relative', width: '100%' }}>
          <input
            type={type}
            name={name}
            value={value}
            onChange={onChange}
            style={{
              width: '100%',
              padding: '10px',
              paddingRight: isValid ? '32px' : '10px',
              border: `1px solid ${showErrors && error ? '#B91C1C' : isValid ? '#10B981' : '#E5E7EB'}`,
              borderRadius: '8px',
              backgroundColor: disabled ? '#F3F4F6' : 'white'
            }}
            required={required}
            disabled={disabled}
            min={min}
            readOnly={readOnly}
            placeholder={placeholder}
          />
          {isValid && (
            <div
              style={{
                position: 'absolute',
                right: '10px',
                top: '50%',
                transform: 'translateY(-50%)',
                pointerEvents: 'none'
              }}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#10B981"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                <polyline points="22 4 12 14.01 9 11.01" />
              </svg>
            </div>
          )}
        </div>
      )}
      {showErrors && error && (
        <p style={{ color: '#B91C1C', fontSize: '12px', marginTop: '4px' }}>{error}</p>
      )}
    </div>
  );
};


const CountrySelect = ({ name, value, onChange, required = false, error, showErrors = false }) => {
  const countryList = Object.values(countries.getNames("en", { select: "official" })).sort();
  // Determine if field is valid (has value and no error)
  const isValid = value && !error && required;

  return (
    <div style={{ width: '100%', maxWidth: '100%', marginBottom: '16px' }}>
      <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: (showErrors && error) ? '#B91C1C' : '#374151', marginBottom: '4px' }}>
        Country {required && <span style={{ color: '#EF4444' }}>*</span>}
      </label>
      <div style={{ position: 'relative', width: '100%' }}>
        <select
          name={name}
          value={value}
          onChange={onChange}
          style={{
            width: '100%',
            padding: '10px',
            paddingRight: isValid ? '32px' : '10px', // Add padding for success icon
            border: `1px solid ${(showErrors && error) ? '#B91C1C' : isValid ? '#10B981' : '#E5E7EB'}`,
            borderRadius: '8px',
            appearance: 'none',  // Remove default select styling
            backgroundColor: 'white'
          }}
          required={required}
        >
          <option value="">Select a country</option>
          {countryList.map((country) => (
            <option key={country} value={country}>
              {country}
            </option>
          ))}
        </select>
        {/* Dropdown indicator */}
        <div
          style={{
            position: 'absolute',
            right: '10px',
            top: '50%',
            transform: 'translateY(-50%)',
            pointerEvents: 'none'
          }}
        >
          {isValid ? (
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#10B981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
              <polyline points="22 4 12 14.01 9 11.01"></polyline>
            </svg>
          ) : (
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="6 9 12 15 18 9"></polyline>
            </svg>
          )}
        </div>
      </div>
      {showErrors && error && <p style={{ color: '#B91C1C', fontSize: '12px', marginTop: '4px' }}>{error}</p>}
    </div>
  );
};

const SelectField = ({ label, name, value, onChange, options, required = false, error, showErrors = false }) => {
  // Determine if field is valid (has value and no error)
  const isValid = value && !error && required;

  return (
    <div style={{ width: '100%', maxWidth: '100%', marginBottom: '16px' }}>
      <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: (showErrors && error) ? '#B91C1C' : '#374151', marginBottom: '4px' }}>
        {label} {required && <span style={{ color: '#EF4444' }}>*</span>}
      </label>
      <div style={{ position: 'relative', width: '100%' }}>
        <select
          name={name}
          value={value}
          onChange={onChange}
          style={{
            width: '100%',
            padding: '10px',
            paddingRight: isValid ? '32px' : '10px', // Add padding for success icon
            border: `1px solid ${(showErrors && error) ? '#B91C1C' : isValid ? '#10B981' : '#E5E7EB'}`,
            borderRadius: '8px',
            appearance: 'none',  // Remove default select styling
            backgroundColor: 'white'
          }}
          required={required}
        >
          <option value="">{`Select ${label}`}</option>
          {options.map(option => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        {/* Dropdown indicator */}
        <div
          style={{
            position: 'absolute',
            right: '10px',
            top: '50%',
            transform: 'translateY(-50%)',
            pointerEvents: 'none'
          }}
        >
          {isValid ? (
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#10B981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
              <polyline points="22 4 12 14.01 9 11.01"></polyline>
            </svg>
          ) : (
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="6 9 12 15 18 9"></polyline>
            </svg>
          )}
        </div>
      </div>
      {showErrors && error && <p style={{ color: '#B91C1C', fontSize: '12px', marginTop: '4px' }}>{error}</p>}
    </div>
  );
};

const SectionHeader = ({ icon: Icon, title }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px', width: '100%' }}>
    <Icon style={{ width: '20px', height: '20px', color: '#2563EB', flexShrink: 0 }} />
    <h2 style={{ fontSize: '18px', fontWeight: '600', color: '#1F2937', margin: 0, padding: 0 }}>
      {title}
    </h2>
  </div>
);

const AlertBox = ({ type, icon, children }) => {
  const backgroundColor = type === 'warning'
    ? '#FFF8E1'
    : type === 'info'
      ? '#E3F2FD'
      : type === 'error'
        ? '#FFEBEE'
        : '#E8F5E9';

  const borderColor = type === 'warning'
    ? '#FFC107'
    : type === 'info'
      ? '#2196F3'
      : type === 'error'
        ? '#F44336'
        : '#4CAF50';

  const textColor = type === 'warning'
    ? '#856404'
    : type === 'info'
      ? '#0D47A1'
      : type === 'error'
        ? '#B71C1C'
        : '#1B5E20';

  return (
    <div
      style={{
        backgroundColor,
        borderLeftWidth: '4px',
        borderLeftStyle: 'solid',
        borderLeftColor: borderColor,
        padding: '12px',
        marginBottom: '16px',
        borderRadius: '0 4px 4px 0',
        width: '100%',
        overflowWrap: 'break-word'
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', width: '100%' }}>
        {icon && <span style={{ marginRight: '8px', flexShrink: 0 }}>{icon}</span>}
        <span
          style={{
            fontSize: '14px',
            color: textColor,
            flexGrow: 1
          }}
        >
          {children}
        </span>
      </div>
    </div>
  );
};

// Button component for navigation
const Button = ({ type = "button", onClick, disabled = false, primary = true, children, style = {} }) => (
  <button
    type={type}
    onClick={onClick}
    disabled={disabled}
    style={{
      backgroundColor: disabled
        ? '#9CA3AF'
        : primary ? '#2563EB' : 'white',
      color: primary ? 'white' : '#2563EB',
      padding: '12px 16px',
      borderRadius: '8px',
      border: primary ? 'none' : '1px solid #2563EB',
      fontSize: '16px',
      fontWeight: '500',
      cursor: disabled ? 'not-allowed' : 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '100%',
      transition: 'all 0.2s ease',
      boxShadow: primary ? '0 1px 3px rgba(0,0,0,0.12)' : 'none',
      ...style
    }}
  >
    {children}
  </button>
);

// Progress bar component
const ProgressBar = ({ currentStep, totalSteps }) => {
  const percent = (currentStep / totalSteps) * 100;

  return (
    <div style={{
      width: '100%',
      marginBottom: '24px',
      marginTop: '8px'
    }}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        marginBottom: '8px',
        fontSize: '14px',
        color: '#6B7280'
      }}>
        <span>Step {currentStep} of {totalSteps}</span>
        <span>{Math.round(percent)}% Complete</span>
      </div>
      <div style={{
        height: '8px',
        backgroundColor: '#E5E7EB',
        borderRadius: '4px',
        overflow: 'hidden'
      }}>
        <div style={{
          height: '100%',
          width: `${percent}%`,
          backgroundColor: '#2563EB',
          borderRadius: '4px',
          transition: 'width 0.3s ease'
        }} />
      </div>
    </div>
  );
};

// Initial form state
const initialFormState = {
  bookingCode: '',
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  gender: '',
  dateOfBirth: '',
  street: '',
  apartment: '',
  zip: '',
  city: '',
  country: '',
  idNumber: '',
  checkIn: '',
  checkOut: '',
  signature: null,
  photo: null,
  submitted: false,
  additionalGuests: [
    { firstName: '', lastName: '', dateOfBirth: '' },
    { firstName: '', lastName: '', dateOfBirth: '' },
    { firstName: '', lastName: '', dateOfBirth: '' }
  ]
};

const MultiPageGuestForm = ({ bookingCode }) => {
  const [formData, setFormData] = useState({
    ...initialFormState,
    bookingCode: bookingCode || ''
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [dateError, setDateError] = useState('');
  const [currentStep, setCurrentStep] = useState(1);
  const totalSteps = 5;
  // Tracks if each step is valid
  const [stepsValidity, setStepsValidity] = useState({
    1: false, // Booking details
    2: false, // Personal info
    3: false, // Address
    4: false, // Additional guests
    5: false  // Document upload & signature
  });
  // Field-level errors for inline error messages
  const [fieldErrors, setFieldErrors] = useState({});
  // New state to track whether to show validation errors
  const [showErrors, setShowErrors] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (bookingCode) {
      fetchFormDetails(bookingCode);
    } else {
      setLoading(false);
    }
  }, [bookingCode]);

  const fetchFormDetails = async (code) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/forms/${code}`);
      const data = await response.json();

      if (data.success && data.form) {
        // Convert legacy format to new format if needed
        const formWithAdditionalGuests = convertLegacyForm(data.form);
        setFormData(formWithAdditionalGuests);
        validateAllSteps(formWithAdditionalGuests);
      } else {
        setError('Form not found. Please check the booking code.');
      }
    } catch (err) {
      console.error('Error fetching form:', err);
      setError('Error fetching form. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Convert old numbered guest fields to array format
  // Helper function to validate email format
  const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Helper function to determine if a field is required
  const isRequiredField = (fieldName) => {
    const requiredFields = [
      'firstName', 'lastName', 'email', 'phone', 'gender', 'dateOfBirth',
      'idNumber', 'street', 'zip', 'city', 'country', 'checkIn', 'checkOut'
    ];
    return requiredFields.includes(fieldName);
  };

  // Helper function to get a human-readable field label
  const getFieldLabel = (fieldName) => {
    const labels = {
      firstName: 'First Name',
      lastName: 'Last Name',
      email: 'Email',
      phone: 'Phone Number',
      gender: 'Gender',
      dateOfBirth: 'Date of Birth',
      idNumber: 'ID Number',
      street: 'Street & Number',
      zip: 'ZIP Code',
      city: 'City',
      country: 'Country',
      checkIn: 'Check-in Date',
      checkOut: 'Check-out Date'
    };
    return labels[fieldName] || fieldName;
  };

  // Add a new function to validate individual fields in real-time
  const validateField = (fieldName, fieldValue) => {
    let error = '';

    // Field-specific validation logic
    if (!fieldValue && isRequiredField(fieldName)) {
      error = `${getFieldLabel(fieldName)} is required.`;
    }

    // Special validation for specific fields
    if (fieldName === 'email' && fieldValue && !isValidEmail(fieldValue)) {
      error = 'Please enter a valid email address.';
    }

    if (fieldName === 'checkOut' && formData.checkIn && fieldValue) {
      const start = new Date(formData.checkIn);
      const end = new Date(fieldValue);
      if (end < start) {
        error = 'Check-out date must be after check-in date.';
        setDateError('Check-out date must be after check-in date');
      } else {
        setDateError('');
      }
    }

    // Update field errors regardless of showErrors state for validation
    // This keeps track of errors internally but only displays when needed
    setFieldErrors(prev => ({
      ...prev,
      [fieldName]: error
    }));
  };

  const convertLegacyForm = (oldForm) => {
    if (!oldForm.additionalGuests) {
      const additionalGuests = [];

      for (let i = 1; i <= 3; i++) {
        additionalGuests.push({
          firstName: oldForm[`firstNameAdditionalGuest${i}`] || '',
          lastName: oldForm[`lastNameAdditionalGuest${i}`] || '',
          dateOfBirth: oldForm[`dateOfBirthAdditionalGuest${i}`] || ''
        });
      }

      // Remove old fields
      const {
        firstNameAdditionalGuest1, lastNameAdditionalGuest1, dateOfBirthAdditionalGuest1,
        firstNameAdditionalGuest2, lastNameAdditionalGuest2, dateOfBirthAdditionalGuest2,
        firstNameAdditionalGuest3, lastNameAdditionalGuest3, dateOfBirthAdditionalGuest3,
        ...restForm
      } = oldForm;

      return {
        ...restForm,
        additionalGuests
      };
    }

    return oldForm;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    const updatedFormData = { ...formData, [name]: value };
    setFormData(updatedFormData);

    // Perform real-time validation for this specific field
    validateField(name, value);

    // Update overall step validity
    validateCurrentStep(updatedFormData);
  };

  const handleDateChange = (e) => {
    const { name, value } = e.target;
    const updatedFormData = { ...formData, [name]: value };

    // Validate the field in real-time
    validateField(name, value);

    // Update form data
    setFormData(updatedFormData);

    // Validate current step
    validateCurrentStep(updatedFormData);
  };

  const handleAdditionalGuestChange = (index, field, value) => {
    const updatedFormData = { ...formData };
    updatedFormData.additionalGuests[index] = {
      ...updatedFormData.additionalGuests[index],
      [field]: value
    };

    setFormData(updatedFormData);

    // Validate the additional guest fields in real-time
    const hasAnyField = updatedFormData.additionalGuests[index].firstName ||
      updatedFormData.additionalGuests[index].lastName ||
      updatedFormData.additionalGuests[index].dateOfBirth;

    if (hasAnyField) {
      if (!updatedFormData.additionalGuests[index].firstName) {
        setFieldErrors(prev => ({ ...prev, [`guest${index}-firstName`]: 'First Name required.' }));
      } else {
        setFieldErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors[`guest${index}-firstName`];
          return newErrors;
        });
      }

      if (!updatedFormData.additionalGuests[index].lastName) {
        setFieldErrors(prev => ({ ...prev, [`guest${index}-lastName`]: 'Last Name required.' }));
      } else {
        setFieldErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors[`guest${index}-lastName`];
          return newErrors;
        });
      }

      if (!updatedFormData.additionalGuests[index].dateOfBirth) {
        setFieldErrors(prev => ({ ...prev, [`guest${index}-dateOfBirth`]: 'Date of Birth required.' }));
      } else {
        setFieldErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors[`guest${index}-dateOfBirth`];
          return newErrors;
        });
      }
    } else {
      // If no fields are filled, clear all errors for this guest
      setFieldErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[`guest${index}-firstName`];
        delete newErrors[`guest${index}-lastName`];
        delete newErrors[`guest${index}-dateOfBirth`];
        return newErrors;
      });
    }

    validateCurrentStep(updatedFormData);
  };

  const handleSignature = (signatureData) => {
    const updatedFormData = { ...formData, signature: signatureData };
    setFormData(updatedFormData);
    // Keine Validierung hier!
  };

  const handleFileUpload = (file) => {
    const updatedFormData = { ...formData, photo: file };
    setFormData(updatedFormData);
    // Keine Validierung hier!
  };

  // Validation functions now only set errors if showErrors is true or explicitly told to
  const validateStep1 = (data, explicitShowError = false) => {
    const errors = {};
    if (!data.checkIn || data.checkIn.trim() === '') {
      errors.checkIn = 'Check-in date is required.';
    }
    if (!data.checkOut || data.checkOut.trim() === '') {
      errors.checkOut = 'Check-out date is required.';
    }
    if (data.checkIn && data.checkOut) {
      const start = new Date(data.checkIn);
      const end = new Date(data.checkOut);
      if (isNaN(start.getTime()) || isNaN(end.getTime()) || end < start) {
        errors.checkOut = 'Check-out date must be after check-in date.';
      }
    }

    if (explicitShowError || showErrors) {
      setFieldErrors(prev => ({ ...prev, ...errors }));
    }

    return Object.keys(errors).length === 0 && !dateError;
  };

  const validateStep2 = (data, explicitShowError = false) => {
    const errors = {};
    if (!data.firstName) errors.firstName = 'First Name is required.';
    if (!data.lastName) errors.lastName = 'Last Name is required.';
    if (!data.email) errors.email = 'Email is required.';
    if (!data.phone) errors.phone = 'Phone Number is required.';
    if (!data.gender) errors.gender = 'Gender is required.';
    if (!data.dateOfBirth) errors.dateOfBirth = 'Date of Birth is required.';
    if (!data.idNumber) errors.idNumber = 'ID Number is required.';

    // Only update error state if explicitShowError or showErrors is true
    if (explicitShowError || showErrors) {
      setFieldErrors(prev => ({ ...prev, ...errors }));
    }

    return Object.keys(errors).length === 0;
  };

  const validateStep3 = (data, explicitShowError = false) => {
    const errors = {};
    if (!data.street) errors.street = 'Street & Number are required.';
    if (!data.zip) errors.zip = 'ZIP Code is required.';
    if (!data.city) errors.city = 'City is required.';
    if (!data.country) errors.country = 'Country is required.';

    if (explicitShowError || showErrors) {
      setFieldErrors(prev => ({ ...prev, ...errors }));
    }
    return Object.keys(errors).length === 0;
  };

  const validateStep4 = (data, explicitShowError = false) => {
    // Check if any additional guest has some fields filled but not all
    let isValid = true;
    const errors = {};

    data.additionalGuests.forEach((guest, index) => {
      const hasAnyField = guest.firstName || guest.lastName || guest.dateOfBirth;
      const hasAllFields = guest.firstName && guest.lastName && guest.dateOfBirth;

      if (hasAnyField && !hasAllFields) {
        isValid = false;
        if (!guest.firstName) {
          errors[`guest${index}-firstName`] = 'First Name required.';
        }
        if (!guest.lastName) {
          errors[`guest${index}-lastName`] = 'Last Name required.';
        }
        if (!guest.dateOfBirth) {
          errors[`guest${index}-dateOfBirth`] = 'Date of Birth required.';
        }
      }
    });

    if (explicitShowError || showErrors) {
      setFieldErrors(prev => ({ ...prev, ...errors }));
    }

    return isValid;
  };

  const validateStep5 = (data, explicitShowError = false) => {
    const errors = {};
    if (!data.photo) errors.photo = 'Photo upload is required.';
    if (!data.signature) errors.signature = 'Signature is required.';

    if (explicitShowError || showErrors) {
      setFieldErrors(prev => ({ ...prev, ...errors }));
    }

    return Object.keys(errors).length === 0;
  };

  const validateCurrentStep = (updatedFormData) => {
    let isValid = false;
    switch (currentStep) {
      case 1:
        isValid = validateStep1(updatedFormData, false);
        break;
      case 2:
        isValid = validateStep2(updatedFormData, false);
        break;
      case 3:
        isValid = validateStep3(updatedFormData, false);
        break;
      case 4:
        isValid = validateStep4(updatedFormData, false);
        break;
      case 5:
        isValid = validateStep5(updatedFormData, false);
        break;
      default:
        break;
    }

    setStepsValidity({
      ...stepsValidity,
      [currentStep]: isValid
    });
  };

  const validateAllSteps = (data) => {
    setStepsValidity({
      1: validateStep1(data, false),
      2: validateStep2(data, false),
      3: validateStep3(data, false),
      4: validateStep4(data, false),
      5: validateStep5(data, false)
    });
  };

  // Validate on initial load and when step changes
  useEffect(() => {
    validateCurrentStep(formData);
    // Reset showErrors when changing steps
    setShowErrors(false);
  }, [currentStep]);

  // Also validate whenever form data changes
  useEffect(() => {
    validateCurrentStep(formData);
  }, [formData]);

  const nextStep = () => {
    // Set showErrors to true to display validation messages
    setShowErrors(true);

    // Validate current step before proceeding
    let isValid = false;
    switch (currentStep) {
      case 1:
        isValid = validateStep1(formData, true);
        break;
      case 2:
        isValid = validateStep2(formData, true);
        break;
      case 3:
        // Step 3 is now handled by the travel companion buttons
        isValid = validateStep3(formData, true);
        break;
      case 4:
        isValid = validateStep4(formData, true);
        break;
      case 5:
        isValid = validateStep5(formData, true);
        break;
      default:
        isValid = true;
        break;
    }

    if (isValid) {
      window.scrollTo(0, 0);

      // For step 3, we don't automatically proceed as we now have buttons
      // for choosing between traveling alone or with others
      if (currentStep === 3) {
        // Keep on same step, let the buttons handle navigation
        return;
      }

      setCurrentStep(prev => Math.min(prev + 1, totalSteps));
      // Reset showErrors when moving to next step
      setShowErrors(false);
    } else {
      // Find the first field with an error and scroll to it
      scrollToFirstError();
    }
  };

  // Function to scroll to the first field with an error
  const scrollToFirstError = () => {
    setTimeout(() => {
      // Look for fields with errors in the current step
      const fieldWithError = Object.keys(fieldErrors)[0];
      if (fieldWithError) {
        const errorElement = document.getElementsByName(fieldWithError)[0];

        if (errorElement) {
          // Scroll to the element with a small offset for better visibility
          errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
          // Focus the field to make it even more obvious
          errorElement.focus();
          return;
        }
      }

      // Handle special cases like photo and signature which might not have a name attribute
      if (currentStep === 5) {
        if (fieldErrors.photo) {
          const photoSection = document.querySelector('[data-error="photo-upload"]');
          if (photoSection) {
            photoSection.scrollIntoView({ behavior: 'smooth', block: 'center' });
            return;
          }
        }

        if (fieldErrors.signature) {
          const signatureSection = document.querySelector('[data-error="signature-pad"]');
          if (signatureSection) {
            signatureSection.scrollIntoView({ behavior: 'smooth', block: 'center' });
            return;
          }
        }
      }

      // Additional guests special case
      if (currentStep === 4) {
        const guestErrorKeys = Object.keys(fieldErrors).filter(key => key.startsWith('guest'));
        if (guestErrorKeys.length > 0) {
          const guestIndex = guestErrorKeys[0].split('-')[0].replace('guest', '');
          const guestSection = document.querySelector(`[data-guest-index="${guestIndex}"]`);
          if (guestSection) {
            guestSection.scrollIntoView({ behavior: 'smooth', block: 'center' });
            return;
          }
        }
      }

      // If we couldn't find a specific error field, at least scroll to the top of the form
      const formContainer = document.querySelector('form');
      if (formContainer) {
        formContainer.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100); // Small delay to ensure DOM is updated
  };

  const prevStep = () => {
    window.scrollTo(0, 0);
    setCurrentStep(prev => Math.max(prev - 1, 1));
    // Reset showErrors when moving to previous step
    setShowErrors(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    setShowErrors(true);
    setSubmitting(true);

    // Final validation check across all steps
    const allValid = Object.values(stepsValidity).every(valid => valid);
    if (!allValid) {
      const invalidSteps = Object.entries(stepsValidity)
        .filter(([_, valid]) => !valid)
        .map(([step, _]) => `Step ${step}`);
      alert(`Please complete the following steps:\n${invalidSteps.join('\n')}`);
      setSubmitting(false);
      return;
    }

    try {
      // Convert additionalGuests array back to legacy format for API
      const legacyFormat = {
        ...formData,
        firstNameAdditionalGuest1: formData.additionalGuests[0].firstName,
        lastNameAdditionalGuest1: formData.additionalGuests[0].lastName,
        dateOfBirthAdditionalGuest1: formData.additionalGuests[0].dateOfBirth,
        firstNameAdditionalGuest2: formData.additionalGuests[1].firstName,
        lastNameAdditionalGuest2: formData.additionalGuests[1].lastName,
        dateOfBirthAdditionalGuest2: formData.additionalGuests[1].dateOfBirth,
        firstNameAdditionalGuest3: formData.additionalGuests[2].firstName,
        lastNameAdditionalGuest3: formData.additionalGuests[2].lastName,
        dateOfBirthAdditionalGuest3: formData.additionalGuests[2].dateOfBirth,
      };

      const { additionalGuests, ...dataToSend } = legacyFormat;

      const dataWithPhoto = {
        ...dataToSend,
        photo: formData.photo
          ? {
            data: formData.photo.base64,
            contentType: formData.photo.mimeType
          }
          : null
      };

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/forms/submit/${bookingCode || 'new'}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(dataWithPhoto)
        }
      );

      const data = await response.json();

      if (data.success) {
        setSuccessMessage('Form submitted successfully!');
        setFormData(prevData => ({ ...prevData, submitted: true }));
        setCurrentStep(totalSteps + 1);
        setShowErrors(false);
      } else {
        alert('Error submitting form: ' + data.message);
      }
    } catch (err) {
      console.error('Error submitting form:', err);
      alert('Error submitting form. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return (
    <div style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '100vh',
      backgroundColor: '#F9FAFB',
      flexDirection: 'column'
    }}>
      <div style={{
        width: '40px',
        height: '40px',
        border: '4px solid #E5E7EB',
        borderTop: '4px solid #2563EB',
        borderRadius: '50%',
        animation: 'spin 1s linear infinite',
      }} />
      <p style={{ marginTop: '16px', color: '#4B5563' }}>Loading your form...</p>
      <style>{`
      @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }
    `}</style>
    </div>
  );

  if (error) return (
    <div style={{
      textAlign: 'center',
      padding: '40px 0',
      color: '#EF4444',
      backgroundColor: '#F9FAFB',
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center'
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '8px',
        boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
        padding: '24px',
        maxWidth: '400px',
        width: '90%'
      }}>
        <h2 style={{ color: '#EF4444', marginTop: 0 }}>Error</h2>
        <p>{error}</p>
      </div>
    </div>
  );

  return (
    <>
      {/* Inject global styles */}
      <style>{globalStyles}</style>

      <div
        style={{
          minHeight: '100vh',
          backgroundColor: '#F9FAFB',
          width: '100%',
          paddingTop: '24px',
          paddingBottom: '24px'
        }}
      >
        <div
          style={{
            width: '100%',
            maxWidth: '768px',
            margin: '0 auto',
            padding: '0 16px'
          }}
        >
          <div
            style={{
              backgroundColor: 'white',
              borderRadius: '8px',
              boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1), 0 1px 2px rgba(0, 0, 0, 0.06)',
              width: '100%',
              padding: '24px'
            }}
          >
            {/* Header */}
            <div style={{ textAlign: 'center', marginBottom: '24px' }}>
              <h1 style={{ fontSize: '24px', fontWeight: 'bold', color: '#111827', margin: '0 0 8px 0' }}>
                Guest Registration Form
              </h1>
              <p style={{ fontSize: '14px', color: '#6B7280' }}>
                Please complete all sections
                {currentStep === 1 && <span style={{ display: 'inline-block', marginLeft: '8px' }}>
                  <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="10"></circle>
                      <polyline points="12 6 12 12 16 14"></polyline>
                    </svg>
                    <span style={{ marginLeft: '4px' }}>Less than 3 minutes to complete</span>
                  </span>
                </span>}
              </p>
            </div>

            {/* Progress bar */}
            {currentStep <= totalSteps && (
              <ProgressBar
                currentStep={currentStep}
                totalSteps={totalSteps}
              />
            )}

            {/* Form content */}
            <form onSubmit={handleSubmit} style={{ width: '100%' }}>
              <fieldset
                disabled={formData.submitted}
                style={{ border: 'none', padding: 0, margin: 0 }}
              >
                {/* Step 1: Booking Information */}
                {currentStep === 1 && (
                  <div>
                    <div style={{ marginBottom: '24px', width: '100%' }}>
                      <InputField
                        label="Booking Code"
                        name="bookingCode"
                        type="text"
                        value={formData.bookingCode}
                        onChange={handleInputChange}
                        readOnly={true}
                        disabled={true}
                        placeholder="Enter your booking code"
                        showErrors={showErrors}
                      />
                    </div>

                    {/* Stay Period */}
                    <div style={{ marginBottom: '24px', width: '100%' }}>
                      <SectionHeader icon={Calendar} title="Stay Period" />
                      <div
                        style={{
                          backgroundColor: '#F9FAFB',
                          border: '1px solid #E5E7EB',
                          borderRadius: '8px',
                          padding: '16px',
                          marginBottom: '16px',
                          width: '100%'
                        }}
                      >
                        <div
                          className="mobile-block"
                          style={{ display: 'flex', flexWrap: 'wrap', gap: '16px', width: '100%' }}
                        >
                          <div style={{ flex: '1 1 100%' }}>
                            <InputField
                              label="Check-in Date"
                              name="checkIn"
                              type="date"
                              value={formData.checkIn}
                              onChange={handleDateChange}
                              min={new Date().toISOString().split('T')[0]}
                              required={true}
                              placeholder="Select check-in date"
                              error={fieldErrors.checkIn}
                              showErrors={showErrors}
                            />
                          </div>

                          <div style={{ flex: '1 1 100%' }}>
                            <InputField
                              label="Check-out Date"
                              name="checkOut"
                              type="date"
                              value={formData.checkOut}
                              onChange={handleDateChange}
                              min={formData.checkIn || new Date().toISOString().split('T')[0]}
                              required={true}
                              placeholder="Select check-out date"
                              error={fieldErrors.checkOut}
                              showErrors={showErrors}
                            />
                          </div>
                        </div>

                        {dateError && (
                          <AlertBox
                            type="error"
                            icon={
                              <svg
                                style={{ width: '20px', height: '20px', color: '#EF4444' }}
                                viewBox="0 0 20 20"
                                fill="currentColor"
                              >
                                <path
                                  fillRule="evenodd"
                                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm-1-9v4a1 1 0 102 0V9a1 1 0 10-2 0zm0-4a1 1 0 112 0 1 1 0 01-2 0z"
                                  clipRule="evenodd"
                                />
                              </svg>
                            }
                          >
                            {dateError}
                          </AlertBox>
                        )}

                        {formData.checkIn && formData.checkOut && !dateError && (
                          <AlertBox
                            type="info"
                            icon={
                              <svg
                                style={{ width: '20px', height: '20px', color: '#3B82F6' }}
                                viewBox="0 0 20 20"
                                fill="currentColor"
                              >
                                <path
                                  fillRule="evenodd"
                                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-11a1 1 0 10-2 0v4a1 1 0 102 0V7zm-1-5a1 1 0 011 1v.01a1 1 0 11-2 0V3a1 1 0 011-1z"
                                  clipRule="evenodd"
                                />
                              </svg>
                            }
                          >
                            Stay duration: {Math.ceil((new Date(formData.checkOut) - new Date(formData.checkIn)) / (1000 * 60 * 60 * 24))} nights
                          </AlertBox>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Step 2: Personal Information */}
                {currentStep === 2 && (
                  <div>
                    <div style={{ marginBottom: '24px', width: '100%' }}>
                      <SectionHeader icon={UserCircle} title="Personal Information" />
                      <div
                        className="mobile-block"
                        style={{ display: 'flex', flexWrap: 'wrap', gap: '16px', width: '100%' }}
                      >
                        <div style={{ flex: '1 1 100%' }}>
                          <InputField
                            label="First Name"
                            name="firstName"
                            type="text"
                            value={formData.firstName}
                            onChange={handleInputChange}
                            required={true}
                            error={fieldErrors.firstName}
                            showErrors={showErrors}
                          />
                        </div>

                        <div style={{ flex: '1 1 100%' }}>
                          <InputField
                            label="Last Name"
                            name="lastName"
                            type="text"
                            value={formData.lastName}
                            onChange={handleInputChange}
                            required={true}
                            error={fieldErrors.lastName}
                            showErrors={showErrors}
                          />
                        </div>

                        <div style={{ flex: '1 1 100%' }}>
                          <InputField
                            label="Email"
                            name="email"
                            type="email"
                            value={formData.email}
                            onChange={handleInputChange}
                            required={true}
                            error={fieldErrors.email}
                            showErrors={showErrors}
                          />
                        </div>

                        <div style={{ flex: '1 1 100%' }}>
                          <InputField
                            label="Phone Number"
                            name="phone"
                            type="tel"
                            value={formData.phone}
                            onChange={handleInputChange}
                            required={true}
                            error={fieldErrors.phone}
                            showErrors={showErrors}
                          />
                        </div>

                        <div style={{ flex: '1 1 100%' }}>
                          <SelectField
                            label="Gender"
                            name="gender"
                            value={formData.gender}
                            onChange={handleInputChange}
                            options={[
                              { value: 'male', label: 'Male' },
                              { value: 'female', label: 'Female' },
                              { value: 'other', label: 'Other' }
                            ]}
                            required={true}
                            error={fieldErrors.gender}
                            showErrors={showErrors}
                          />
                        </div>

                        <div style={{ flex: '1 1 100%' }}>
                          <InputField
                            label="Date of Birth"
                            name="dateOfBirth"
                            type="date"
                            value={formData.dateOfBirth}
                            onChange={handleInputChange}
                            required={true}
                            placeholder="Select date of birth"
                            error={fieldErrors.dateOfBirth}
                            showErrors={showErrors}
                          />
                        </div>

                        <div style={{ width: '100%' }}>
                          <InputField
                            label="ID Number (passport, driving license...)"
                            name="idNumber"
                            type="text"
                            value={formData.idNumber}
                            onChange={handleInputChange}
                            required={true}
                            error={fieldErrors.idNumber}
                            showErrors={showErrors}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Step 3: Home Address */}
                {currentStep === 3 && (
                  <div>
                    <div style={{ marginBottom: '24px', width: '100%' }}>
                      <SectionHeader icon={MapPin} title="Home Address" />
                      <div style={{ width: '100%', marginBottom: '16px' }}>
                        <AlertBox type="warning">
                          ⚠️ Please provide your permanent home address in your home country
                        </AlertBox>
                      </div>
                      <div
                        className="mobile-block"
                        style={{ display: 'flex', flexWrap: 'wrap', gap: '16px', width: '100%' }}
                      >
                        <div style={{ width: '100%' }}>
                          <InputField
                            label="Street & Number"
                            name="street"
                            type="text"
                            value={formData.street}
                            onChange={handleInputChange}
                            required={true}
                            error={fieldErrors.street}
                            showErrors={showErrors}
                          />
                        </div>

                        <div style={{ flex: '1 1 100%' }}>
                          <InputField
                            label="Address line 2 (optional)"
                            name="apartment"
                            type="text"
                            value={formData.apartment}
                            onChange={handleInputChange}
                            showErrors={showErrors}
                          />
                        </div>

                        <div style={{ flex: '1 1 100%' }}>
                          <InputField
                            label="ZIP Code"
                            name="zip"
                            type="text"
                            value={formData.zip}
                            onChange={handleInputChange}
                            required={true}
                            error={fieldErrors.zip}
                            showErrors={showErrors}
                          />
                        </div>

                        <div style={{ flex: '1 1 100%' }}>
                          <InputField
                            label="City"
                            name="city"
                            type="text"
                            value={formData.city}
                            onChange={handleInputChange}
                            required={true}
                            error={fieldErrors.city}
                            showErrors={showErrors}
                          />
                        </div>

                        <div style={{ flex: '1 1 100%' }}>
                          <CountrySelect
                            name="country"
                            value={formData.country}
                            onChange={handleInputChange}
                            required={true}
                            error={fieldErrors.country}
                            showErrors={showErrors}
                          />
                        </div>
                      </div>
                    </div>

                    {/* Travel companions selection */}
                    <div style={{ marginTop: '32px', width: '100%' }}>
                      <SectionHeader icon={UserPlus} title="Travel Companions" />
                      <div style={{ width: '100%', marginBottom: '16px' }}>
                        <AlertBox type="info">
                          ℹ️ Please indicate whether you are traveling alone or with others
                        </AlertBox>
                      </div>
                      <div style={{
                        display: 'flex',
                        flexDirection: window.innerWidth < 640 ? 'column' : 'row',
                        gap: '16px',
                        width: '100%'
                      }}>
                        <Button
                          onClick={() => {
                            // Clear any additional guests data
                            const clearedGuests = formData.additionalGuests.map(() => ({
                              firstName: '', lastName: '', dateOfBirth: ''
                            }));
                            setFormData({
                              ...formData,
                              additionalGuests: clearedGuests
                            });
                            // Skip to step 5 (document upload)
                            setCurrentStep(5);
                            setShowErrors(false);
                          }}
                          primary={false}
                          style={{
                            flex: '1',
                            backgroundColor: '#F3F4F6',
                            borderColor: '#E5E7EB',
                            color: '#374151'
                          }}
                        >
                          I'm Traveling Alone
                        </Button>
                        <Button
                          onClick={() => {
                            // First validate the address fields
                            setShowErrors(true);
                            const isValid = validateStep3(formData, true);
                            if (isValid) {
                              // If valid, proceed to step 4 (additional guests)
                              setCurrentStep(4);
                              setShowErrors(false);
                            }
                          }}
                          style={{ flex: '1' }}
                        >
                          I'm Traveling with Companions
                        </Button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Step 4: Additional Travelers */}
                {currentStep === 4 && (
                  <div>
                    <div style={{ marginBottom: '24px', width: '100%' }}>
                      <SectionHeader icon={UserPlus} title="Additional guests (optional)" />
                      <div style={{ marginBottom: '16px', width: '100%' }}>
                        <AlertBox type="info">
                          ℹ️ Fill in details only for additional travelers staying with you. Leave empty if traveling alone.
                        </AlertBox>
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', width: '100%' }}>
                        {formData.additionalGuests.map((guest, index) => (
                          <div
                            key={index}
                            data-guest-index={index}
                            style={{
                              padding: '16px',
                              backgroundColor: '#F9FAFB',
                              borderRadius: '8px',
                              width: '100%'
                            }}
                          >
                            <h3 style={{ fontSize: '16px', fontWeight: '500', color: '#4B5563', marginBottom: '16px' }}>
                              Additional Traveler {index + 1}
                            </h3>
                            <div className="mobile-block" style={{ display: 'flex', flexWrap: 'wrap', gap: '16px', width: '100%' }}>
                              <div style={{ flex: '1 1 100%' }}>
                                <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: (showErrors && fieldErrors[`guest${index}-firstName`]) ? '#B91C1C' : '#374151', marginBottom: '4px' }}>
                                  First Name
                                </label>
                                <input
                                  type="text"
                                  name={`guest${index}-firstName`}
                                  value={guest.firstName}
                                  onChange={(e) => handleAdditionalGuestChange(index, 'firstName', e.target.value)}
                                  style={{
                                    width: '100%',
                                    padding: '10px',
                                    border: `1px solid ${(showErrors && fieldErrors[`guest${index}-firstName`]) ? '#B91C1C' : '#E5E7EB'}`,
                                    borderRadius: '8px'
                                  }}
                                  placeholder="First name"
                                />
                                {showErrors && fieldErrors[`guest${index}-firstName`] && <p style={{ color: '#B91C1C', fontSize: '12px', marginTop: '4px' }}>{fieldErrors[`guest${index}-firstName`]}</p>}
                              </div>
                              <div style={{ flex: '1 1 100%' }}>
                                <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: (showErrors && fieldErrors[`guest${index}-lastName`]) ? '#B91C1C' : '#374151', marginBottom: '4px' }}>
                                  Last Name
                                </label>
                                <input
                                  type="text"
                                  name={`guest${index}-lastName`}
                                  value={guest.lastName}
                                  onChange={(e) => handleAdditionalGuestChange(index, 'lastName', e.target.value)}
                                  style={{
                                    width: '100%',
                                    padding: '10px',
                                    border: `1px solid ${(showErrors && fieldErrors[`guest${index}-lastName`]) ? '#B91C1C' : '#E5E7EB'}`,
                                    borderRadius: '8px'
                                  }}
                                  placeholder="Last name"
                                />
                                {showErrors && fieldErrors[`guest${index}-lastName`] && <p style={{ color: '#B91C1C', fontSize: '12px', marginTop: '4px' }}>{fieldErrors[`guest${index}-lastName`]}</p>}
                              </div>
                              <div style={{ width: '100%' }}>
                                <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: (showErrors && fieldErrors[`guest${index}-dateOfBirth`]) ? '#B91C1C' : '#374151', marginBottom: '4px' }}>
                                  Date of Birth
                                </label>
                                <div style={{ position: 'relative', width: '100%' }}>
                                  <input
                                    type="date"
                                    name={`guest${index}-dateOfBirth`}
                                    value={guest.dateOfBirth}
                                    onChange={(e) => handleAdditionalGuestChange(index, 'dateOfBirth', e.target.value)}
                                    style={{
                                      width: '100%',
                                      padding: '10px',
                                      border: `1px solid ${(showErrors && fieldErrors[`guest${index}-dateOfBirth`]) ? '#B91C1C' : '#E5E7EB'}`,
                                      borderRadius: '8px',
                                      color: guest.dateOfBirth ? '#000' : '#9CA3AF'
                                    }}
                                    placeholder="Date of birth"
                                    onFocus={(e) => e.target.showPicker && e.target.showPicker()}
                                  />
                                  {!guest.dateOfBirth && (
                                    <div
                                      style={{
                                        position: 'absolute',
                                        right: '10px',
                                        top: '50%',
                                        transform: 'translateY(-50%)',
                                        pointerEvents: 'none'
                                      }}
                                    >
                                      <Calendar size={16} color="#9CA3AF" />
                                    </div>
                                  )}
                                </div>
                                {showErrors && fieldErrors[`guest${index}-dateOfBirth`] && <p style={{ color: '#B91C1C', fontSize: '12px', marginTop: '4px' }}>{fieldErrors[`guest${index}-dateOfBirth`]}</p>}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {/* Step 5: Documents and Signature */}
                {currentStep === 5 && (
                  <div>
                    {/* Document Upload */}
                    <div style={{ marginBottom: '24px', width: '100%' }} data-error="photo-upload">
                      <SectionHeader icon={Upload} title="Document Upload" />
                      <div style={{ marginBottom: '16px', width: '100%' }}>
                        <AlertBox type="info">
                          Please upload a photo of your ID document (passport, ID card, driver's license)
                        </AlertBox>
                      </div>
                      <FileUpload onFileUpload={handleFileUpload} currentFile={formData.photo} />
                      {showErrors && fieldErrors.photo && <p style={{ color: '#B91C1C', fontSize: '12px', marginTop: '4px' }}>{fieldErrors.photo}</p>}
                    </div>

                    {/* Signature */}
                    <div style={{ marginBottom: '24px', width: '100%' }} data-error="signature-pad">
                      <SectionHeader icon={PenTool} title="Signature" />
                      <div style={{ marginBottom: '16px', width: '100%' }}>
                        <AlertBox type="info">
                          Please sign with your finger within the white box
                        </AlertBox>
                      </div>
                      <SignaturePad onSignature={handleSignature} currentSignature={formData.signature} />
                      {showErrors && fieldErrors.signature && <p style={{ color: '#B91C1C', fontSize: '12px', marginTop: '4px' }}>{fieldErrors.signature}</p>}
                    </div>
                  </div>
                )}

                {/* Success/Completion Page */}
                {currentStep > totalSteps && (
                  <div style={{ textAlign: 'center', padding: '40px 0' }}>
                    <div style={{
                      width: '80px',
                      height: '80px',
                      background: '#10B981',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      margin: '0 auto 24px'
                    }}>
                      <Check style={{ width: '40px', height: '40px', color: 'white' }} />
                    </div>
                    <h2 style={{ fontSize: '24px', color: '#111827', marginBottom: '16px' }}>
                      Thank You!
                    </h2>
                    <p style={{ fontSize: '16px', color: '#4B5563', marginBottom: '24px' }}>
                      Your registration form has been successfully submitted.
                    </p>
                    <p style={{ fontSize: '14px', color: '#6B7280' }}>
                      Booking Code: <strong>{formData.bookingCode}</strong>
                    </p>
                  </div>
                )}

                {/* Navigation buttons */}
                {currentStep <= totalSteps && (
                  <div style={{
                    marginTop: '32px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '12px'
                  }}>
                    {/* Only show continue button for steps 1, 2, 4 and 5 */}
                    {currentStep < totalSteps && currentStep !== 3 ? (
                      <Button
                        onClick={nextStep}
                      >
                        Continue <ArrowRight style={{ marginLeft: '8px', width: '16px', height: '16px' }} />
                      </Button>
                    ) : currentStep === totalSteps ? (
                      <Button
                        type="submit"
                        disabled={submitting}
                      >
                        {submitting ? (
                          <span style={{ display: 'flex', alignItems: 'center' }}>
                            <span className="spinner" style={{
                              width: '20px', height: '20px', border: '3px solid #E5E7EB', borderTop: '3px solid #2563EB', borderRadius: '50%', marginRight: '8px', animation: 'spin 1s linear infinite', display: 'inline-block'
                            }} />
                            Submitting...
                          </span>
                        ) : formData.submitted ? (
                          <span style={{ display: 'flex', alignItems: 'center' }}>
                            <Check style={{ width: '20px', height: '20px', marginRight: '8px' }} />
                            Submitted
                          </span>
                        ) : (
                          'Submit Registration'
                        )}
                      </Button>
                    ) : null}

                    {currentStep > 1 && (
                      <Button
                        onClick={prevStep}
                        primary={false}
                      >
                        Back
                      </Button>
                    )}
                  </div>
                )}
              </fieldset>
            </form>
          </div>
        </div>
      </div>
    </>
  );
};

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-10 h-10 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

// App Routes Component (needs access to useAuth hook)
const AppRoutes = () => {
  const { isAuthenticated, loading, signOut } = useAuth();

  const DynamicGuestForm = () => {
    const { bookingCode } = useParams();
    return <MultiPageGuestForm bookingCode={bookingCode} />;
  };

  const handleLogout = async () => {
    await signOut();
  };

  const PageLoader = () => (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="text-center">
        <Loader2 className="w-10 h-10 animate-spin text-blue-600 mx-auto mb-4" />
        <p className="text-gray-600">Loading...</p>
      </div>
    </div>
  );

  // Guest form route should always be accessible without waiting for auth
  // Only show loading state for admin routes
  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        {/* Public routes - no auth required */}
        <Route path="/" element={<LandingPage />} />
        <Route path="/form/:bookingCode" element={<DynamicGuestForm />} />

        {/* Auth routes - show login or redirect */}
        <Route
          path="/login"
          element={
            loading ? (
              <PageLoader />
            ) : isAuthenticated ? (
              <Navigate to="/admin" replace />
            ) : (
              <AuthLogin />
            )
          }
        />

        {/* Protected routes - require authentication */}
        <Route
          path="/admin"
          element={
            <ProtectedRoute>
              <AdminDashboard onLogout={handleLogout} />
            </ProtectedRoute>
          }
        />
        <Route
          path="/form-details/:bookingCode"
          element={
            <ProtectedRoute>
              <FormDetails />
            </ProtectedRoute>
          }
        />
      </Routes>
    </Suspense>
  );
};

const App = () => {
  return (
    <AuthProvider>
      <Router>
        <AppRoutes />
      </Router>
    </AuthProvider>
  );
};

export default App;